import { createFileRoute, Link } from "@tanstack/react-router";
import { EMAIL } from "@/lib/contacto";

export const Route = createFileRoute("/privacidad")({
  component: Privacidad,
  head: () => ({
    meta: [
      { title: "Política de privacidad | Pyme Visible" },
      {
        name: "description",
        content:
          "Qué datos recibe Pyme Visible por el formulario de contacto, para qué se usan, quiénes participan técnicamente y cómo ejercer tus derechos.",
      },
      { property: "og:title", content: "Política de privacidad | Pyme Visible" },
      {
        property: "og:description",
        content: "Cómo tratamos los datos de contacto que recibimos en Pyme Visible.",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "es_CL" },
      { property: "og:url", content: "https://pymevisible.cl/privacidad" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Política de privacidad | Pyme Visible" },
      {
        name: "twitter:description",
        content: "Cómo tratamos los datos de contacto que recibimos en Pyme Visible.",
      },
    ],
    links: [{ rel: "canonical", href: "https://pymevisible.cl/privacidad" }],
  }),
});

function Privacidad() {
  return (
    <main id="contenido" lang="es-CL" className="pv-legal-page mx-auto max-w-3xl px-5 py-20">
      <Link to="/" className="text-sm font-medium text-primary underline">
        ← Volver al inicio
      </Link>
      <h1 className="mt-6 text-3xl font-extrabold text-navy sm:text-4xl">Política de privacidad</h1>

      <div className="pv-legal-copy mt-8 grid gap-8 leading-relaxed text-muted-foreground">
        <p>Esta información es general y no reemplaza una asesoría legal.</p>

        <section>
          <h2 className="text-xl font-bold text-navy">Quién es responsable</h2>
          <p className="mt-3">
            Este sitio es operado por Álvaro Silva González, en adelante “el responsable”. Para
            consultas sobre compras, datos personales, cambios o garantías, puedes escribir a:{" "}
            <a className="font-medium text-primary underline" href={`mailto:${EMAIL}`}>
              {EMAIL}
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">Qué datos recibimos</h2>
          <p className="mt-3">
            Cuando completas el formulario de contacto recibimos tu nombre, tu teléfono o WhatsApp y
            el servicio que te interesa. De forma opcional puedes agregar el nombre de tu negocio,
            un mensaje y tu correo electrónico. No pedimos más datos que esos.
          </p>
          <p className="mt-3">
            No solicitamos datos sensibles (por ejemplo, salud, creencias, situación
            socioeconómica). Te pedimos no enviarlos por este formulario ni por WhatsApp.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">Para qué los usamos</h2>
          <p className="mt-3">
            Usamos tu información exclusivamente para responder tu solicitud, preparar y gestionar
            una eventual contratación y mantener la continuidad del trabajo que acordemos. No los
            usamos para marketing ni los vendemos.
          </p>
          <p className="mt-3">
            La base operativa del tratamiento es tu propia solicitud y el consentimiento que
            entregas al enviar el formulario, según corresponda.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">Minimización, seguridad y conservación</h2>
          <p className="mt-3">
            Pedimos únicamente los datos necesarios para contactarte y aplicamos medidas razonables
            de seguridad, con acceso limitado a las personas que participan en la atención de tu
            solicitud.
          </p>
          <p className="mt-3">
            Conservamos los datos necesarios para procesar compras, coordinar instalaciones, atender
            solicitudes de postventa y cumplir obligaciones legales. Los antecedentes asociados a
            documentos tributarios podrán conservarse hasta por seis años. Los demás datos serán
            eliminados o anonimizados cuando ya no sean necesarios para estas finalidades, salvo que
            la ley exija o permita conservarlos por más tiempo.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">Proveedores que participan</h2>
          <ul className="mt-4 grid list-disc gap-2 pl-5">
            <li>
              <strong className="text-navy">FormSubmit</strong>: procesa técnicamente el envío del
              formulario y nos hace llegar tu mensaje por correo.
            </li>
            <li>
              <strong className="text-navy">Google (Analytics)</strong>: sólo se activa si aceptas
              la categoría de analítica en el panel de preferencias.
            </li>
            <li>
              <strong className="text-navy">Google (Maps)</strong>: el mapa incrustado sólo se carga
              si eliges cargar contenido externo.
            </li>
          </ul>
          <p className="mt-3">
            Estos proveedores operan con infraestructura fuera de Chile, por lo que la información
            que procesan puede transferirse y almacenarse en otros países, conforme a sus propias
            políticas.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">Analítica y cookies</h2>
          <p className="mt-3">
            Guardamos tu elección de privacidad en el almacenamiento local de tu navegador. Si
            retiras el consentimiento, evitamos futuras cargas de esos servicios; las cookies que ya
            hubieran quedado en tu navegador puedes gestionarlas o eliminarlas desde su
            configuración. Detalles en la{" "}
            <Link to="/cookies" className="font-medium text-primary underline">
              Política de cookies
            </Link>
            .
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">Tus derechos</h2>
          <p className="mt-3">
            Puedes pedirnos acceder a tus datos, rectificarlos, eliminarlos, bloquearlos u oponerte
            a su uso. Escríbenos a{" "}
            <a className="font-medium text-primary underline" href={`mailto:${EMAIL}`}>
              {EMAIL}
            </a>
            ; respondemos por la misma vía.
          </p>
        </section>
      </div>
    </main>
  );
}
